import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PetsIcon from '@mui/icons-material/Pets';
import PhoneIcon from '@mui/icons-material/Phone';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import { Box, Button, DialogContent, DialogTitle, IconButton, Dialog, Typography } from '@mui/material';

import { formatPhoneNumber } from '@shared-forms-web/components/MaskedPhoneNumberInput/MaskedPhoneNumberInput';

import { Appointment } from '@src/components/UserPortal/UserPortal.types';
import { dialogStyles } from '@src/components/UserPortal/UserPortal.styles';
import { AppointmentTypeName, AppointmentName } from '@src/components/ReasonForVisit/ReasonForVisit.helpers';

import WarningIcon from '@src/icons/WarningIcon';

import { APP_PATH } from '@src/constants';

function formatAppointmentDateTime(isoString: string): string {
	const date = new Date(isoString);
	const dateStr = date.toLocaleDateString('en-US', {
		weekday: 'long',
		month: 'long',
		day: 'numeric',
		year: 'numeric',
	});
	const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
	return `${dateStr} at ${timeStr}`;
}

type AppointmentConflictDialogProps = {
	open: boolean;
	onClose: () => void;
	appointment: Appointment;
	petName: string;
};

export default function AppointmentConflictDialog({
	open,
	onClose,
	appointment,
	petName,
}: Readonly<AppointmentConflictDialogProps>) {
	const facilityName = appointment.location?.displayName ?? '';
	const facilityPhone = appointment.location?.phoneNumber ?? null;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			aria-labelledby="appointment-conflict-title"
			data-test-id="appointment-conflict-modal"
			sx={dialogStyles}
		>
			<DialogTitle id="appointment-conflict-title">
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Typography variant="header2" data-test-id="appointment-conflict-modal-heading">
						You&apos;re already booked
					</Typography>
					<IconButton
						onClick={onClose}
						aria-label="Close"
						size="small"
						data-test-id="appointment-conflict-modal-close"
					>
						<CloseIcon />
					</IconButton>
				</Box>
			</DialogTitle>
			<DialogContent>
				<Typography variant="bodyMediumBook" data-test-id="appointment-conflict-modal-body">
					We already have your pet scheduled for a{' '}
					{appointment.type?.name === AppointmentName.Sick ||
					appointment.type?.name === AppointmentName.Urgent
						? 'sick or urgent'
						: 'preventative care'}{' '}
					appointment. Please edit your appointment time in our client portal.
				</Typography>
				<Box
					display="flex"
					flexDirection="column"
					gap={1.5}
					data-test-id="appointment-conflict-existing-details"
					sx={{ p: '0.75rem', border: '1px solid #DADADA', borderRadius: '8px', my: 2.5 }}
				>
					<Typography
						variant="bodyMediumBook"
						color="text.tertiary"
						data-test-id="appointment-conflict-existing-label"
					>
						Existing Appointment
					</Typography>
					<Box display="flex" alignItems="center" gap={1}>
						<PetsIcon sx={{ color: 'tertiary.main', fontSize: 24 }} />
						<Typography variant="bodyMediumBook" data-test-id="appointment-conflict-pet-name">
							{petName}
						</Typography>
					</Box>
					<Box display="flex" alignItems="center" gap={1}>
						<MedicalServicesIcon sx={{ color: 'tertiary.main', fontSize: 24 }} />
						<Typography variant="bodyMediumBook" data-test-id="appointment-conflict-appointment-type">
							{AppointmentTypeName[appointment.type?.name ?? '']}
						</Typography>
					</Box>
					<Box display="flex" alignItems="center" gap={1}>
						<CalendarTodayIcon sx={{ color: 'tertiary.main', fontSize: 24 }} />
						<Typography variant="bodyMediumBook" data-test-id="appointment-conflict-date-time">
							{formatAppointmentDateTime(appointment.start)}
						</Typography>
					</Box>
					<Box display="flex" alignItems="center" gap={1}>
						<LocationOnOutlinedIcon sx={{ color: 'tertiary.main', fontSize: 24 }} />
						<Typography variant="bodyMediumBook" data-test-id="appointment-conflict-location">
							{facilityName}
						</Typography>
					</Box>
				</Box>
				<Button
					variant="primaryDefault"
					size="M"
					component="a"
					href={`${APP_PATH}/user-portal/appointment/${appointment.id}`}
					data-test-id="appointment-conflict-continue-button"
					fullWidth
				>
					Edit in Client Portal
				</Button>
				{facilityPhone && (
					<>
						<Box
							display="flex"
							flexDirection="column"
							gap={1}
							mb={2}
							mt={5}
							data-test-id="appointment-conflict-modal-call-text"
						>
							<Box display="flex" alignItems="center" gap={1}>
								<WarningIcon sx={{ size: 20 }} />
								<Typography variant="bodyMediumSemiBold" color="text.primary">
									Has your pet&apos;s condition changed?
								</Typography>
							</Box>
							<Typography variant="bodyMediumBook" color="text.secondary">
								If your pet&apos;s condition has worsened, please give us a call.
							</Typography>
						</Box>
						<Button
							variant="secondaryDefault"
							size="M"
							component="a"
							href={`tel:${facilityPhone}`}
							startIcon={<PhoneIcon sx={{ fontSize: 20 }} />}
							data-test-id="appointment-conflict-call-button"
							fullWidth
						>
							{formatPhoneNumber(facilityPhone)}
						</Button>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
}
